import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface BenefitType {
  id: number;
  type_code: string;
  type_name: string;
  description: string | null;
}

interface BenefitTypeForm {
  type_code: string;
  type_name: string;
  description: string;
}

const emptyForm: BenefitTypeForm = { type_code: '', type_name: '', description: '' };

export function BenefitTypes() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<BenefitType[] | null>(null);
  const [editing, setEditing] = useState<'new' | BenefitType | null>(null);
  const [form, setForm] = useState<BenefitTypeForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<BenefitType[]>('/employee-benefit-types').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: BenefitType) => {
    setForm({ type_code: item.type_code, type_name: item.type_name, description: item.description ?? '' });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof BenefitTypeForm) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/employee-benefit-types', form);
      else if (editing) await api.put(`/employee-benefit-types/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: BenefitType) => {
    if (!(await confirmDelete(`Delete "${item.type_name}"?`))) return;
    try {
      await api.delete(`/employee-benefit-types/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Benefit Types</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.type_code}</td>
                  <td>{item.type_name}</td>
                  <td className="muted">{item.description ?? '—'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No benefit types yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add benefit type' : 'Edit benefit type'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Code *
              <input value={form.type_code} onChange={set('type_code')} required />
              {errors.type_code && <small className="error">{errors.type_code[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.type_name} onChange={set('type_name')} required />
              {errors.type_name && <small className="error">{errors.type_name[0]}</small>}
            </label>
            <label className="span-2">
              Description
              <textarea rows={2} value={form.description} onChange={set('description')} />
              {errors.description && <small className="error">{errors.description[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
